import useFormFeedback from "@/lib/useFormFeedback";
import { useRef } from 'react';
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { API } from "@/App";

const CONTACT_METHODS = ["Phone", "Text Message", "Email"];
const CLEANING_PLANS = {
  "1": [
    "1 can, 1 time a month, $10",
    "1 can, 2 times a month, $20",
    "1 can, 4 times a month, $30",
  ],
  "2": [
    "2 cans, 1 time a month, $20",
    "2 cans, 2 times a month, $40",
    "2 cans, 4 times a month, $60",
  ],
  "3": [
    "3 cans, 1 time a month, $25",
    "3 cans, 2 times a month, $50",
    "3 cans, 4 times a month, $75",
  ],
};

export default function TrashCanCleaningPage({ embedded = false }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    serviceAddress: "",
    city: "",
    preferredContactMethod: "Text Message",
    cansToClean: "3",
    servicePlan: "3 cans, 1 time a month, $25",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { setError, fieldProps, fieldError, errorSummary } = useFormFeedback("cleaning");

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const availablePlans = CLEANING_PLANS[form.cansToClean] || CLEANING_PLANS["3"];

  const updateCansToClean = (value) => {
    const nextValue = String(Math.min(3, Math.max(1, Number(value) || 1)));
    setForm((prev) => ({
      ...prev,
      cansToClean: nextValue,
      servicePlan: CLEANING_PLANS[nextValue][0],
    }));
  };

  const inquiryId = useRef(crypto.randomUUID());
  const submit = async () => {
    if (!form.fullName.trim()) return setError("Please enter your full name.", "cleaning-fullName");
    if (!form.email.trim()) return setError("Please enter your email address.", "cleaning-email");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError("Please enter a valid email address.", "cleaning-email");
    if (!form.phone.trim()) return setError("Please enter your phone number.", "cleaning-phone");
    if (form.phone.replace(/\D/g, "").length < 10) return setError("Please enter a valid phone number.", "cleaning-phone");
    if (!form.serviceAddress.trim()) return setError("Please enter the service address.", "cleaning-serviceAddress");
    if (!form.city.trim()) return setError("Please enter your city.", "cleaning-city");

    setSubmitting(true);
    setError("");
    try {
      const message = [
        "Trash Can Cleaning Signup Request",
        `Preferred contact method: ${form.preferredContactMethod}`,
        `Service plan: ${form.servicePlan}`,
        `Service address: ${form.serviceAddress}`,
        `City: ${form.city}`,
        form.notes ? `Special notes: ${form.notes}` : null,
      ].filter(Boolean).join("\n");

      const response = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submission_id: inquiryId.current,
          full_name: form.fullName,
          email: form.email,
          phone: form.phone,
          service_needed: "Trash Can Cleaning",
          service_area: form.city,
          message,
        }),
      });
      if (!response.ok) throw new Error("Could not submit your cleaning signup right now. Please call us directly.");
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {!embedded && <>
      <SEO
        title="Trash Can Cleaning Signup | Junk In Da Truck"
        description="Sign up for Trash Can Cleaning with Junk In Da Truck. Monthly cleaning for 3 cans for $25."
        path="/trash-can-cleaning"
      />
      <Header />

      <section className="relative pt-24 sm:pt-28 bg-[#0D0D0D] overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="/trash-can-cleaning.jpg" alt="Trash Can Cleaning" className="w-full h-full object-cover" />
        </div>
        <div className="relative mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center rounded-full bg-[#97BF04]/15 px-4 py-2 text-sm font-semibold text-[#97BF04] border border-[#97BF04]/20 mb-5">
              New Recurring Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Trash Can Cleaning Service
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 leading-relaxed max-w-2xl">
              Keep your cans cleaner, fresher, and easier to deal with. Choose the can count and cleaning frequency that fits your property.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white font-medium mt-6 bg-black/40 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 max-w-xl">
              <span>$25 monthly</span>
              <span>Up to 3 cans</span>
              <span>Great valet add-on</span>
            </div>
          </div>
        </div>
      </section>
      </>}

      <section className="py-14 sm:py-16 bg-[#F8F8F4]">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.05fr,0.95fr] gap-8 lg:gap-10">
          <div className="bg-white rounded-3xl border border-[#0D0D0D]/8 shadow-lg p-6 sm:p-8">
            {submitted ? (
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#97BF04]/15 px-4 py-2 text-sm font-semibold text-[#2C4001]">
                  <CheckCircle2 className="w-4 h-4" /> Signup received
                </div>
                <h2 className="text-3xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  You are on the list.
                </h2>
                <p className="text-[#4B5563] leading-relaxed">
                  We got your Trash Can Cleaning request and will follow up shortly to confirm details.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="tel:+17724945048"><Button className="bg-[#0D0D0D] text-white hover:bg-[#1a1a1a]"><Phone className="w-4 h-4 mr-2" /> Call Us</Button></a>
                  <a href="/services/trash-can-cleaning"><Button variant="outline">Back to Service Page</Button></a>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-semibold text-[#0D0D0D] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Trash Can Cleaning Signup
                </h2>
                <p className="text-[#4B5563] mb-6 leading-relaxed">
                  Choose the can count and monthly frequency that fits your property, then send your signup and we will follow up.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="cleaning-fullName" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Full Name *</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-fullName")} required aria-required="true" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} placeholder="Your full name" />
                    {fieldError("cleaning-fullName")}
                  </div>
                  <div>
                    <label htmlFor="cleaning-email" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Email *</label>
                    <input type="email" className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-email")} required aria-required="true" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@example.com" />
                    {fieldError("cleaning-email")}
                  </div>
                  <div>
                    <label htmlFor="cleaning-phone" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Phone *</label>
                    <input type="tel" className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-phone")} required aria-required="true" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="Best number to reach you" />
                    {fieldError("cleaning-phone")}
                  </div>
                  <div>
                    <label htmlFor="cleaning-preferredContactMethod" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Preferred Contact Method *</label>
                    <select className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-preferredContactMethod")} required aria-required="true" value={form.preferredContactMethod} onChange={(e) => update('preferredContactMethod', e.target.value)}>
                      {CONTACT_METHODS.map((method) => <option key={method}>{method}</option>)}
                    </select>
                    {fieldError("cleaning-preferredContactMethod")}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cleaning-serviceAddress" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Service Address *</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-serviceAddress")} required aria-required="true" value={form.serviceAddress} onChange={(e) => update('serviceAddress', e.target.value)} placeholder="Street address" />
                    {fieldError("cleaning-serviceAddress")}
                  </div>
                  <div>
                    <label htmlFor="cleaning-city" className="block text-sm font-semibold text-[#0D0D0D] mb-2">City *</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-city")} required aria-required="true" value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Vero Beach, Sebastian, etc." />
                    {fieldError("cleaning-city")}
                  </div>
                  <div>
                    <label htmlFor="cleaning-cansToClean" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Number of Cans *</label>
                    <select className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-cansToClean")} required aria-required="true" value={form.cansToClean} onChange={(e) => updateCansToClean(e.target.value)}>
                      <option value="1">1 can</option>
                      <option value="2">2 cans</option>
                      <option value="3">3 cans</option>
                    </select>
                    {fieldError("cleaning-cansToClean")}
                  </div>
                  <div>
                    <label htmlFor="cleaning-servicePlan" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Cleaning Plan *</label>
                    <select className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-servicePlan")} required aria-required="true" value={form.servicePlan} onChange={(e) => update('servicePlan', e.target.value)}>
                      {availablePlans.map((plan) => <option key={plan} value={plan}>{plan.replace(/^\d+ cans?, /, "")}</option>)}
                    </select>
                    {fieldError("cleaning-servicePlan")}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cleaning-selectedPlan" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Selected Plan</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-[#F8F8F4] px-4 py-3" {...fieldProps("cleaning-selectedPlan")} value={form.servicePlan} readOnly />
                    {fieldError("cleaning-selectedPlan")}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cleaning-notes" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Special Notes</label>
                    <textarea className="w-full min-h-[120px] rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("cleaning-notes")} value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Anything else we should know?" />
                    {fieldError("cleaning-notes")}
                  </div>
                </div>

                {errorSummary}

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button onClick={submit} disabled={submitting} className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] font-semibold gap-2">
                    {submitting ? 'Submitting...' : 'Request Trash Can Cleaning Signup'} <ArrowRight className="w-4 h-4" />
                  </Button>
                  <a href="tel:+17724945048"><Button variant="outline" className="font-semibold gap-2"><Phone className="w-4 h-4" /> Call (772) 494-5048</Button></a>
                </div>
              </>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-[#0D0D0D]/8 shadow-lg p-6 sm:p-8">
              <h3 className="text-2xl font-semibold text-[#0D0D0D] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                What is included
              </h3>
              <ul className="space-y-3 text-[#4B5563] leading-relaxed">
                <li>• 1, 2, or 4 cleanings per month</li>
                <li>• 1-can, 2-can, or 3-can options</li>
                <li>• Helps reduce smell and grime</li>
                <li>• Easy add-on to Trash Can Valet</li>
              </ul>
            </div>
            <div className="bg-[#0D0D0D] rounded-3xl text-white p-6 sm:p-8">
              <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Also want curb help?
              </h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you want your cans rolled out and returned too, pair this with Trash Can Valet.
              </p>
              <a href="/services/trash-can-valet" className="inline-flex items-center gap-2 text-[#97BF04] font-semibold hover:text-[#b7e21a] transition-colors">
                View Trash Can Valet <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {!embedded && <Footer />}
    </div>
  );
}
